import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  ChangelogSortDirection,
  createDefaultChangelogFilter,
  isChangelogSortProperty,
  type ChangelogFilter,
  type ChangelogSortProperty,
} from '../../changelog/model';

export interface ChangelogRouteSearch {
  publisherIds?: string;
  placementIds?: string;
  integrationIds?: string;
  demandPartnerIds?: string;
  createdBy?: string;
  lineItemIds?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
  sortProperty?: ChangelogSortProperty | null;
  sortDirection?: ChangelogSortDirection;
}

export function changelogRouteSearchSchema(search: Record<string, unknown>): ChangelogRouteSearch {
  const rawSortProperty = optionalTrimmedString(search['sortProperty']);
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const sortProperty =
    rawSortProperty && isChangelogSortProperty(rawSortProperty) ? rawSortProperty : undefined;
  const sortDirection = Object.values(ChangelogSortDirection).includes(
    search['sortDirection'] as ChangelogSortDirection,
  )
    ? (search['sortDirection'] as ChangelogSortDirection)
    : undefined;
  return {
    ...optionalRawString(search, 'publisherIds'),
    ...optionalRawString(search, 'placementIds'),
    ...optionalRawString(search, 'integrationIds'),
    ...optionalRawString(search, 'demandPartnerIds'),
    ...optionalRawString(search, 'createdBy'),
    ...optionalRawString(search, 'lineItemIds'),
    ...optionalRawString(search, 'dateFrom'),
    ...optionalRawString(search, 'dateTo'),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

export function parseChangelogRouteSearch(
  search: ChangelogRouteSearch,
  referenceDate = new Date(),
): ChangelogFilter {
  const defaults = createDefaultChangelogFilter(referenceDate);
  return {
    publisherIds: parseStringList(search.publisherIds),
    placementIds: parseStringList(search.placementIds),
    integrationIds: parseStringList(search.integrationIds),
    demandPartnerIds: parseStringList(search.demandPartnerIds),
    createdBy: search.createdBy ?? defaults.createdBy,
    lineItemIds: parseStringList(search.lineItemIds),
    dateFrom: search.dateFrom ?? defaults.dateFrom,
    dateTo: search.dateTo ?? defaults.dateTo,
    page: search.page ? search.page - 1 : defaults.page,
    size: search.size ?? defaults.size,
    sortProperty:
      search.sortProperty === null ? null : (search.sortProperty ?? defaults.sortProperty),
    sortDirection: search.sortDirection ?? defaults.sortDirection,
  };
}

export function serializeChangelogRouteSearch(
  filter: ChangelogFilter,
  referenceDate = new Date(),
): ChangelogRouteSearch {
  const defaults = createDefaultChangelogFilter(referenceDate);
  return {
    ...(filter.publisherIds.length ? { publisherIds: filter.publisherIds.join(',') } : {}),
    ...(filter.placementIds.length ? { placementIds: filter.placementIds.join(',') } : {}),
    ...(filter.integrationIds.length ? { integrationIds: filter.integrationIds.join(',') } : {}),
    ...(filter.demandPartnerIds.length
      ? { demandPartnerIds: filter.demandPartnerIds.join(',') }
      : {}),
    ...(filter.createdBy ? { createdBy: filter.createdBy } : {}),
    ...(filter.lineItemIds.length ? { lineItemIds: filter.lineItemIds.join(',') } : {}),
    ...(filter.dateFrom !== defaults.dateFrom ? { dateFrom: filter.dateFrom } : {}),
    ...(filter.dateTo !== defaults.dateTo ? { dateTo: filter.dateTo } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaults.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaults.sortProperty ? { sortProperty: filter.sortProperty } : {}),
    ...(filter.sortDirection !== defaults.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}

export function countActiveChangelogFilters(filter: ChangelogFilter) {
  return [
    filter.publisherIds.length > 0,
    filter.placementIds.length > 0,
    filter.integrationIds.length > 0,
    filter.demandPartnerIds.length > 0,
    Boolean(filter.createdBy),
    filter.lineItemIds.length > 0,
  ].filter(Boolean).length;
}

function optionalRawString(
  search: Record<string, unknown>,
  key: keyof ChangelogRouteSearch,
): Partial<ChangelogRouteSearch> {
  return typeof search[key] === 'string' ? { [key]: search[key] } : {};
}

function parseStringList(value: string | undefined) {
  if (!value) return [];
  return [
    ...new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}
